<template>
  <v-col class="register default-slot" md="7" sm="12">
    <div class="top-icons">
      <router-link class="back-icon" to="/">
        <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back to home
      </router-link>
    </div>
    <h1 class="register-title mb-3">New Affiliate Application</h1>
    <div class="text-left mx-3">
      <p>
        We hereby apply to become an Affiliate Club of the Australian Shepherd
        Club of America. If approved by the ASCA Board of Directors, this Club
        agrees to abide by the obligations of an Affiliate Club, the <ASCAArtIncLink />, <BylawsLink />, Dispute Rules and all rules,
        regulations, and guidelines pertaining to the Registry, and all program
        rules, including but not limited to, Agility, Conformation, Juniors,
        Obedience, Rally, Stock Dog, and Tracking programs.
      </p>
      <p>
        This Club agrees to conscientiously promote the best interests of the
        Australian Shepherd and the Australian Shepherd Club of America on a
        local level.
      </p>
      <p>
        We understand affiliate dues are based on a current calendar year and we
        shall be entitled to full rights and privileges for that period.
        Further, we understand affiliate dues are due and payable to ASCA on or
        before the 31st of December each year. To become an ASCA Affiliate,
        include the following:
      </p>
    </div>
    <ol class="text-left">
      <li>
        List of all officers/directors with their current e-mail address,
        mailing address, and ASCA membership number with expiration date. (The
        only offices that can be combined are Secretary/Treasurer. All other
        offices must be held by a different member.)
      </li>
      <li>
        List of all members with their current e-mail address and mailing
        address.
      </li>
      <li>Affiliation fee of ${{ price }} (payment form on last screen).</li>
    </ol>
    <v-form ref="addressForm" v-model="isValid" class="register-form mt-3">
      <v-text-field
        outlined
        v-model="clubName"
        label="Affiliate Name"
        :error-messages="nameMatchErrorMessage"
        :rules="[(v) => !!v || 'Affiliate name is required']"
      ></v-text-field>
      <country-dropdown
        :selectedCountry="address.country"
        v-on:update="updateCountry"
      />
      <state-dropdown
        v-if="address.country === country_USA"
        required
        :selectedState="address.state"
        v-on:update="address.state = $event"
      />
      <v-text-field
        outlined
        v-model="address.street"
        label="Address"
        :rules="[(v) => !!v || 'Address is required']"
      />
      <v-text-field
        outlined
        v-model="address.city"
        label="City"
        :rules="[validateCity]"
      />
      <v-text-field
        v-if="address.country && address.country !== country_USA"
        outlined
        v-model="address.international"
        label="International Area"
        :rules="[(v) => !!v || 'International Area is required']"
      ></v-text-field>
      <v-text-field
        outlined
        v-model="address.zip"
        label="Zip Code"
        :rules="[validateZip]"
      ></v-text-field>
      <v-select
        outlined
        v-model="termOfOffice"
        :items="[1, 2, 3]"
        :rules="[(v) => !!v || 'Term of office is required']"
        label="Term of Office (years)"
      ></v-select>
      <v-text-field
        outlined
        v-model="affiliateWebsite"
        label="Affiliate Website"
        :rules="[validateWebsite]"
      ></v-text-field>
    </v-form>

    <v-btn
      class="white--text"
      large
      v-on:click="
        changeFirstPageInfo({
          clubName,
          address,
          termOfOffice,
          affiliateWebsite,
        });
        changePage(2);
      "
      :disabled="!isValid || price === null || nameLookupFailed || !memberTypesLoaded"
      color="primary"
      >NEXT</v-btn
    >
    <p class="steps">STEP <b>1</b> OF <b>3</b></p>
    <footer-text />
  </v-col>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "throttle-debounce";

import { UserInfo } from "@/types/User";
import ASCAArtIncLink from "@/components/common/ASCAArtIncLink.vue";
import BylawsLink from "@/components/common/BylawsLink.vue";
import FooterText from "@/components/common/FooterText.vue";
import CountryDropdown from "@/components/common/inputs/CountryDropdown.vue";
import StateDropdown from "@/components/common/inputs/StateDropdown.vue";
import registerAffiliateService from "@/services/portal/registerAffiliateService";
import normalizeAddressRegion from "@/helpers/normalize-address-region";
import { Countries } from "@/constants";

export default Vue.extend({
  name: "AffiliateAddress",
  components: {
    FooterText,
    CountryDropdown,
    StateDropdown,
    ASCAArtIncLink,
    BylawsLink,
  },
  props: {
    changePage: Function,
    changeFirstPageInfo: Function,
    clubName: String,
    address: Object,
    termOfOffice: Number,
    affiliateWebsite: String,
    price: Number,
    memberTypesLoaded: Boolean,
  },
  data: function () {
    return {
      isValid: true,
      nameMatch: null,
      nameLookupFailed: false,
      country_USA: Countries.USA,    
    };
  },
  watch: {
    clubName: {
      handler: debounce(
        500,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal) return;
          if (newVal) {
            this.nameLookupFailed = true;
            registerAffiliateService
              .getMatchingAffiliateNames(newVal)
              .then((response: String[]) => {
                if (this.clubName !== newVal) return;

                if (response.length > 0) {
                  this.nameMatch = response.join(", ");
                } else {
                  this.nameMatch = null;
                }
                this.nameLookupFailed = false;
              })
              .catch((error) => {
                if (this.clubName === newVal) this.$handleError(error);
              });
          } else {
            this.nameMatch = null;
            this.nameLookupFailed = false;
          }
        }
      ),
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    nameMatchErrorMessage(): string {
      return this.nameMatch
        ? `Name is the same as existing affiliate: ${this.nameMatch}. Please choose another name.`
        : "";
    },
  },
  methods: {
    validateWebsite(input: string) {
      return /^[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(input) || input === '' || "Website is invalid"
    },
    validateCity(v: string) {
      return this.address.country === Countries.USA
        ? !!v || 'City is required'
        : true
    },
    validateZip(v: string) {
      return this.address.country === Countries.USA
        ? !!v || 'Zip/Postal is required'
        : true
    },
    updateCountry: async function (v: string) {
      this.address.country = v;
      const region = normalizeAddressRegion(
        v,
        this.address.state,
        this.address.international
      );
      this.address.state = region.state;
      this.address.international = region.international;

      await Vue.nextTick();

      (this.$refs.addressForm as any).validate();
    },
  },
});
</script>
